import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AccountingComposite } from '../entity/accounting.composite';
import { ClosingComposite } from '../entity/closing.composite';
import { IncomeStatementComposite } from '../entity/incomestatement.composite';
import { IncomeTaxDeclarationComposite } from '../entity/incometaxdeclaration.composite';
import { SalaryComposite } from '../entity/salary.composite';
import { TaxDeclarationComposite } from '../entity/taxdeclaration.composite';
import { withFirstTeamIds } from '../misc/rxjs-operators';
import { toIncomeStatementCompositeTypeTransformer } from '../state/transformers/incomestatement.transformers';
import {
  toAccountingCompositeTypeTransformer,
  toClosingCompositeTypeTransformer,
  toIncomeTaxDeclarationCompositeTypeTransformer,
  toSalaryCompositeTypeTransformer,
  toTaxDeclarationCompositeTypeTransformer,
} from '../state/transformers/transformers';
import {
  AccountingCompositeType,
  AccountingListParams,
  ClosingCompositeType,
  ClosingListParams,
  CompositeListParams,
  IncomeTaxDeclarationCompositeType,
  IncomeTaxDeclarationListParams,
  SalaryCompositeType,
  SalaryListParams,
  TaxDeclarationCompositeType,
  TaxDeclarationListParams,
} from '../state/types';
import { IncomeStatementCompositeType } from '../state/types/incomestatement.types';
import { BLService } from './bl.service';

@Injectable({ providedIn: 'root' })
export class CustomListService extends BLService {
  private RESOURCE_ENDPOINT = 'task-view';

  getAccountingComposites({
    responsibleId,
    executorId,
    searchCriteria,
    year,
    periodIndex,
  }: AccountingListParams): Observable<AccountingComposite[]> {
    const url = `${this.RESOURCE_ENDPOINT}/accounting/${responsibleId}/${executorId}/${searchCriteria}/${year}/${periodIndex}`;
    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.httpGet<AccountingComposite[]>(url, this.httpAuthHeader.getHeaderWithoutBody(ids)).pipe(
          map((result) => result.map((accountingComposite) => AccountingComposite.from(accountingComposite))),
        ),
      ),
    );
  }

  getAccountingCompositeTypes(params: AccountingListParams): Observable<AccountingCompositeType[]> {
    return this.getAccountingComposites(params).pipe(
      map((items) => items.map(toAccountingCompositeTypeTransformer.transform)),
    );
  }

  getSalaryComposites({
    responsibleId,
    executorId,
    year,
    periodIndex,
  }: SalaryListParams): Observable<SalaryComposite[]> {
    const url = `${this.RESOURCE_ENDPOINT}/salary/${responsibleId}/${executorId}/${year}/${periodIndex}`;
    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.httpGet<SalaryComposite[]>(url, this.httpAuthHeader.getHeaderWithoutBody(ids)).pipe(
          map((result) => result.map((salaryComposite) => SalaryComposite.from(salaryComposite))),
        ),
      ),
    );
  }

  getSalaryCompositeTypes(params: SalaryListParams): Observable<SalaryCompositeType[]> {
    return this.getSalaryComposites(params).pipe(map((items) => items.map(toSalaryCompositeTypeTransformer.transform)));
  }

  getTaxdeclarationComposites({
    responsibleId,
    executorId,
    year,
    periodIndex,
  }: TaxDeclarationListParams): Observable<TaxDeclarationComposite[]> {
    const url = `${this.RESOURCE_ENDPOINT}/taxDeclaration/${responsibleId}/${executorId}/${year}/${periodIndex}`;
    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.httpGet<TaxDeclarationComposite[]>(url, this.httpAuthHeader.getHeaderWithoutBody(ids)).pipe(
          map((result) => result.map((taxDeclaration) => TaxDeclarationComposite.from(taxDeclaration))),
        ),
      ),
    );
  }

  getTaxdeclarationCompositeTypes(params: TaxDeclarationListParams): Observable<TaxDeclarationCompositeType[]> {
    return this.getTaxdeclarationComposites(params).pipe(
      map((items) => items.map(toTaxDeclarationCompositeTypeTransformer.transform)),
    );
  }

  getIncomeTaxdeclarationComposites(
    config: IncomeTaxDeclarationListParams,
  ): Observable<IncomeTaxDeclarationComposite[]> {
    const url = `${this.RESOURCE_ENDPOINT}/incomeTaxDeclaration`;
    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.httpPost<IncomeTaxDeclarationComposite[]>(url, config, this.httpAuthHeader.getHeaderWithoutBody(ids)).pipe(
          map((result) =>
            result.map((incomeTaxDeclaration) => IncomeTaxDeclarationComposite.from(incomeTaxDeclaration)),
          ),
        ),
      ),
    );
  }

  getIncomeTaxdeclarationCompositeTypes(
    params: IncomeTaxDeclarationListParams,
  ): Observable<IncomeTaxDeclarationCompositeType[]> {
    return this.getIncomeTaxdeclarationComposites(params).pipe(
      map((items) => items.map(toIncomeTaxDeclarationCompositeTypeTransformer.transform)),
    );
  }

  getIncomeStatementComposites({
    responsibleId,
    executorId,
    year,
  }: CompositeListParams): Observable<IncomeStatementComposite[]> {
    const url = `${this.RESOURCE_ENDPOINT}/incomeStatement/${responsibleId}/${executorId}/${year}`;
    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.httpGet<IncomeStatementComposite[]>(url, this.httpAuthHeader.getHeaderWithoutBody(ids)).pipe(
          map((result) =>
            result.map((incomeStatementComposite) => IncomeStatementComposite.from(incomeStatementComposite)),
          ),
        ),
      ),
    );
  }

  getIncomeStatementCompositeTypes(config: CompositeListParams): Observable<IncomeStatementCompositeType[]> {
    return this.getIncomeStatementComposites(config).pipe(
      map((items) => items.map(toIncomeStatementCompositeTypeTransformer.transform)),
    );
  }

  getClosingComposites(config: ClosingListParams): Observable<ClosingComposite[]> {
    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.httpPost<ClosingComposite[]>(
          `${this.RESOURCE_ENDPOINT}/closing`,
          config,
          this.httpAuthHeader.getHeaderWithoutBody(ids),
        ).pipe(
          map((list) => list.map((item) => ClosingComposite.from(item))), // creates ClosingComposite objects from the response json
        ),
      ),
    );
  }

  getClosingCompositeTypes(config: ClosingListParams): Observable<ClosingCompositeType[]> {
    return this.getClosingComposites(config).pipe(
      map((items) => items.map(toClosingCompositeTypeTransformer.transform)),
    );
  }
}
